// src/components/admin/decisions/RevisePaper.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { 
  Send,
  Mail,
  User,
  FileText,
  X,
  Paperclip,
  AlertCircle,
  CheckCircle,
  Copy,
  Link as LinkIcon,
  Edit,
  RefreshCw,
  Home
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';

const RevisePaper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const { user } = useAuth();
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const manuscriptId = queryParams.get('a');

  // State for manuscript data
  const [manuscriptData, setManuscriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // State for form fields
  const [formData, setFormData] = useState({
    articleId: manuscriptId,
    editor: user?.email || '',
    editorName: user?.fullname || 'ASFI Editorial Office',
    reviewerEmail: '',
    ccEmail: '',
    bccEmail: '',
    subject: '',
  });

  // State for email tags
  const [ccTags, setCcTags] = useState([]);
  const [bccTags, setBccTags] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');

  // State for attachments
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState([]);
  
  // State for UI
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [copySuccess, setCopySuccess] = useState('');

  // Fetch manuscript data
  useEffect(() => {
    const fetchManuscriptData = async () => {
      if (!manuscriptId) {
        setFetchError('No manuscript ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.post('/editors/getSubmissionData', { id: manuscriptId });
        
        if (response.data.error) {
          setFetchError(response.data.error);
          setManuscriptData(null);
        } else if (response.data.success && response.data.articles) {
          const article = response.data.articles;
          
          // Parse authors if it's a JSON string
          let authors = [];
          try {
            authors = typeof article.authors === 'string' 
              ? JSON.parse(article.authors) 
              : article.authors || [];
          } catch (e) {
            authors = [article.corresponding_author_fullname];
          }

          const data = {
            id: article.revision_id,
            title: article.title || 'Untitled',
            correspondingAuthor: article.corresponding_author_fullname || article.corresponding_author || '',
            correspondingEmail: article.corresponding_authors_email || article.corresponding_email || '',
            submittedDate: article.created_at || '',
            type: article.article_type || 'Research Article',
            authors: authors
          };
          
          setManuscriptData(data);
          
          // Update form data with fetched information
          setFormData(prev => ({
            ...prev,
            reviewerEmail: data.correspondingEmail,
            subject: `Request for Revision: ${data.title} (${manuscriptId})`,
          }));
          
          setFetchError(null);
        }
      } catch (error) {
        console.error('Error fetching manuscript:', error);
        setFetchError(error.response?.data?.error || 'Failed to fetch manuscript data');
        setManuscriptData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchManuscriptData();
  }, [manuscriptId]);

  // Initialize Quill editor
  useEffect(() => {
    if (!quillRef.current || !manuscriptData) return;

    // Check if Quill is already initialized
    if (quillInstance.current) {
      return;
    }

    quillInstance.current = new Quill(quillRef.current, {
      modules: {
        toolbar: [
          ['bold', 'italic'],
          ['link', 'blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ 'header': [1, 2, false] }],
          [{ 'color': [] }],
          ['clean']
        ],
      },
      theme: 'snow',
      placeholder: 'Compose your revision request letter...',
    });

    // Load initial content
    updateTemplate();

    return () => {
      // Cleanup if needed
    };
  }, [manuscriptData]);

  // Update template when manuscriptData changes
  useEffect(() => {
    if (quillInstance.current && manuscriptData) {
      updateTemplate();
    }
  }, [manuscriptData, formData.editorName]);

  const updateTemplate = () => {
    if (!quillInstance.current || !manuscriptData) return;
    
    const template = getFilledTemplate();
    quillInstance.current.setContents(template);
  };

  const getFilledTemplate = () => {
    const today = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Deadline is 4 weeks from today (typical for revisions)
    const fourWeeksLater = new Date();
    fourWeeksLater.setDate(fourWeeksLater.getDate() + 28);
    const deadlineDate = fourWeeksLater.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return [
      { insert: `${today}\n\n` },
      { insert: 'Dear ' },
      { attributes: { color: '#e60000', bold: true }, insert: manuscriptData.correspondingAuthor },
      { insert: `,\n\nThank you for submitting your manuscript "${manuscriptData.title}" (${manuscriptId}) to ASFI Research Journal (ASFIRJ). We have now received reviewers' reports on your paper and the reviewers' comments follow at the end of this email. Some of the reviewers also made direct comments to your manuscript and these are attached for your attention.\n\nThe reviewers have recommended a major/minor revision to your manuscript. The editors would be prepared to consider a revised version of the manuscript if you believe you can provide a full and convincing response to the criticisms the reviewers have raised. The revised manuscript will of course be sent back to the original reviewers for further assessment.\n\nAn opportunity to submit a revision of your manuscript does not guarantee acceptance since a revision may still not achieve a high enough priority to warrant publication.\n\nPlease study the comments carefully and amend the manuscript as appropriate. In your response, please ensure that you include all the comments by the reviewers and provide point-by-point reply to each point raised, specifying how you have revised your manuscript. Please attach the point-by-point response as a PDF file.\n\nIn addition to the point-by-point response to the reviewers' comments, we ask that you also upload a clean and marked copy of the revised manuscript. The marked copy should highlight the changes you have made using the 'Tracked Changes' function in Microsoft Word.\n\nPlease note that your paper will not be able to be processed without these three files uploaded (PDF of point-by-point response, clean copy of the revised manuscript, and marked copy of the revised manuscript).\n\nWhen ready with your revision, please upload all the files in your author's dashboard using the revise function aligned to the submitted manuscript.\n\nPlease do not submit your revised manuscript as a new submission.\n\nPlease ensure that the structure of the revised manuscript strictly conforms with ASFIRJ recommended manuscript structure and format. Please carefully consult the authors' instructions here: https://asfirj.org/authors.html.\n\nFailure to conform to ASFIRJ manuscript structure will cause a delay in publishing your paper if it is acceptable for publication in the journal as all formats have to be followed by all authors before publishing.\n\nYou have up to ` },
      { attributes: { color: '#e60000', bold: true }, insert: deadlineDate },
      { insert: ` to submit the revised manuscript.\n\nSincerely,\n\n` },
      { attributes: { color: '#e60000', bold: true }, insert: formData.editorName },
      { insert: `\nAssociate Editor\n\nsubmissions@asfirj.org\n\nASFI Research Journal\nExcellence. Quality. Impact\n"Raising the bar of scientific publishing in Africa"\nhttps://asfirj.org/\nasfirj@asfirj.org\nLinkedIn: www.linkedin.com/in/asfi-research-journal-1b9929309\nX (formerly Twitter): https://twitter.com/asfirj1\nInstagram: https://www.instagram.com/asfirj1/\nWhatsApp: https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3\n` }
    ];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle CC email input
  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addCcEmail();
    } else if (e.key === 'Backspace' && ccInput === '' && ccTags.length > 0) {
      removeCcTag(ccTags.length - 1);
    }
  };

  const handleCcBlur = () => {
    if (ccInput.trim()) {
      addCcEmail();
    }
  };

  const addCcEmail = () => {
    const email = ccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !ccTags.includes(email)) {
      setCcTags([...ccTags, email]);
      setFormData(prev => ({ 
        ...prev, 
        ccEmail: [...ccTags, email].join(', ')
      }));
      setCcInput('');
    }
  };

  // Handle BCC email input
  const handleBccKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addBccEmail();
    } else if (e.key === 'Backspace' && bccInput === '' && bccTags.length > 0) {
      removeBccTag(bccTags.length - 1);
    }
  };

  const handleBccBlur = () => {
    if (bccInput.trim()) {
      addBccEmail();
    }
  };

  const addBccEmail = () => {
    const email = bccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !bccTags.includes(email)) {
      setBccTags([...bccTags, email]);
      setFormData(prev => ({ 
        ...prev, 
        bccEmail: [...bccTags, email].join(', ')
      }));
      setBccInput('');
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const removeCcTag = (indexToRemove) => {
    const newTags = ccTags.filter((_, index) => index !== indexToRemove);
    setCcTags(newTags);
    setFormData(prev => ({ 
      ...prev, 
      ccEmail: newTags.join(', ')
    }));
  };

  const removeBccTag = (indexToRemove) => {
    const newTags = bccTags.filter((_, index) => index !== indexToRemove);
    setBccTags(newTags);
    setFormData(prev => ({ 
      ...prev, 
      bccEmail: newTags.join(', ')
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const totalFiles = attachments.length + files.length;

    if (totalFiles > 10) {
      setMessage({
        type: 'error',
        text: 'Maximum 10 files allowed'
      });
      return;
    }

    setAttachments(prev => [...prev, ...files]);

    // Create preview URLs
    const newPreviews = files.map(file => ({
      name: file.name,
      size: (file.size / 1024).toFixed(2),
      url: URL.createObjectURL(file)
    }));
    setAttachmentPreview(prev => [...prev, ...newPreviews]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setAttachmentPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const handleResetTemplate = () => {
    if (quillInstance.current && manuscriptData) {
      updateTemplate();
      setMessage({
        type: 'success',
        text: 'Template reset with latest data'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const confirmSend = async () => {
    setSending(true);
    setShowConfirmModal(false);

    try {
      // Get editor content - send as the raw Delta ops array
      const editorContent = quillInstance.current.getContents();
      
      // Prepare form data
      const formDataToSend = new FormData();
      formDataToSend.append('articleId', formData.articleId);
      formDataToSend.append('reviewerEmail', formData.reviewerEmail);
      formDataToSend.append('ccEmail', formData.ccEmail);
      formDataToSend.append('bccEmail', formData.bccEmail);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('editorName', formData.editorName);
      
      // Send the entire Delta object
      formDataToSend.append('message', JSON.stringify(editorContent.ops || editorContent));
      formDataToSend.append('decision', 'revise');
      
      attachments.forEach(file => {
        formDataToSend.append('attachments[]', file);
      });

      // Send to the endpoint (you'll need to create this endpoint)
      const response = await axios.post('/editors/email/revisePaper', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout for large files
      });

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: response.data.message || 'Revision request sent successfully'
        });

        setTimeout(() => {
          setMessage({ type: '', text: '' });
          navigate('/editors/dashboard');
        }, 3000);
      } else if (response.data.status === 'warning') {
        setMessage({
          type: 'warning',
          text: response.data.message || 'Email already sent'
        });
        setSending(false);
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Failed to send email'
        });
        setSending(false);
      }

    } catch (error) {
      console.error('Error sending email:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to send revision request. Please try again.'
      });
      setSending(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading manuscript data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error/Not Found state
  if (fetchError || !manuscriptData) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Manuscript Not Found</h3>
            <p className="text-gray-600 mb-6">
              {fetchError || 'No manuscript data found for the provided ID'}
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500">
                <strong>Manuscript ID:</strong> {manuscriptId || 'Not provided'}
              </p>
            </div>
            <button
              onClick={() => navigate('/editors/dashboard')}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : message.type === 'warning'
            ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center">
            <Edit className="w-6 h-6 mr-2 text-blue-600" />
            Request Revision
          </h3>
          <button
            type="button"
            onClick={handleResetTemplate}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            title="Reset template with latest data"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Template
          </button>
        </div>
        <p className="text-gray-600 mt-1">
          Request revisions for manuscript {manuscriptId}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} id="sendMail">
          <input type="hidden" name="articleId" value={manuscriptId} readOnly />

          {/* Recipient Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Recipient</h4>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Corresponding Author Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  name="reviewerEmail"
                  id="email"
                  value={formData.reviewerEmail}
                  onChange={handleInputChange}
                  readOnly
                  required
                />
              </div>

              {/* CC Field with Tags */}
              <div>
                <label htmlFor="ccEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  CC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent min-h-[42px]">
                  {ccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-blue-100 text-blue-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeCcTag(index)}
                        className="ml-1 hover:text-blue-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={handleCcKeyDown}
                    onBlur={handleCcBlur}
                    placeholder={ccTags.length === 0 ? "Enter CC emails (comma separated)" : ""}
                  />
                </div>
              </div>

              {/* BCC Field with Tags */}
              <div>
                <label htmlFor="bccEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  BCC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent min-h-[42px]">
                  {bccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-gray-100 text-gray-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeBccTag(index)}
                        className="ml-1 hover:text-gray-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyDown={handleBccKeyDown}
                    onBlur={handleBccBlur}
                    placeholder={bccTags.length === 0 ? "Enter BCC emails (comma separated)" : ""}
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500">* Press Enter, Space, or Comma to add email addresses</p>
            </div>

            {/* Copyable Information */}
            {manuscriptData && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="text-sm font-medium text-blue-800 mb-3 flex items-center">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Click to Copy
                </h5>
                <ul className="space-y-2">
                  <li>
                    <button
                      type="button"
                      onClick={() => handleCopy(`${manuscriptData.title} (${manuscriptId})`, 'title')}
                      className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      Manuscript Title And ID
                      {copySuccess === 'title' && (
                        <span className="ml-2 text-xs text-green-600">Copied!</span>
                      )}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleCopy(manuscriptData.correspondingAuthor, 'author')}
                      className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-center"
                    >
                      <User className="w-3 h-3 mr-2" />
                      Corresponding Author's Name
                      {copySuccess === 'author' && (
                        <span className="ml-2 text-xs text-green-600">Copied!</span>
                      )}
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Subject Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Subject</h4>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="subject"
              id="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Message Body Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Message Body</h4>
              <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                Auto-filled with manuscript data
              </div>
            </div>
            
            {/* Placeholder Info */}
            {manuscriptData && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Manuscript Title]:</span>
                  <span className="ml-2 text-gray-600 truncate block">{manuscriptData.title.substring(0, 30)}...</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Manuscript ID]:</span>
                  <span className="ml-2 text-gray-600">{manuscriptId}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Corresponding Author]:</span>
                  <span className="ml-2 text-gray-600">{manuscriptData.correspondingAuthor}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Editor Name]:</span>
                  <span className="ml-2 text-gray-600">{formData.editorName}</span>
                </div>
              </div>
            )}

            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Deadline:</strong> Four weeks from today ({new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })})
              </p>
            </div>

            {/* Quill Editor */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div ref={quillRef} style={{ height: '400px' }} />
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              * The message has been automatically populated with manuscript data. Please replace the red placeholders with appropriate information.
            </p>
          </div>

          {/* Attachments Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">
              Attachments (Max: 10 Files)
            </h4>
            
            <input
              type="file"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="attachments[]"
              id="attachmentInput"
              multiple
              onChange={handleFileChange}
              accept="*/*"
            />

            {/* Attachment Preview */}
            {attachmentPreview.length > 0 && (
              <div className="mt-4 space-y-2" id="attachmentPreviewContainer">
                {attachmentPreview.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <Paperclip className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">{file.name}</p>
                        <p className="text-xs text-gray-500">{file.size} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-white rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-center mt-8">
            <button
              type="submit"
              disabled={sending}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Revision Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Edit className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Revision Request</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to request revisions for this manuscript?
                  </p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> The author will need to make revisions based on reviewer comments. The deadline is set to four weeks from today.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSend}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Confirm Revision Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200 mt-8">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default RevisePaper;